import { keyframes, styled } from "goober";

import {
  memo,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { routeConnectors } from "../../lib/routeConnectors";
import { useScrollFade } from "../../hooks/useScrollFade";
import { useTenantCustomDomain } from "../../hooks/useTenantCustomDomain";
import { playPop } from "../../lib/sounds";
import {
  getStepCopyableEmailAddress,
  getStepCopyableUrl,
} from "../../lib/stepUrl";
import { recallStepFile } from "../../lib/lastOpenStepFile";
import { publicDomain, publicUrl } from "../../lib/tenantOrigin";
import { makeUrl, navigate, routes } from "../../lib/urls";
import type { StepData, WorkflowData } from "../Workflow";
import { EffortIcon } from "../EffortIcon";
import { Icon } from "../Icon";
import { Link } from "../Link";
import { PressButton } from "../PressButton";
import { SearchInput } from "../SearchField";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { StepContextMenu } from "./StepContextMenu";
import {
  computeStepMapGeometry,
  DENSITY_SPECS,
  fitStepMap,
  specForSpacing,
  type SpacingLevel,
  type StepMapDensity,
} from "./stepMapGeometry";

// LinkData for StepMap uses resolved version IDs (transformed from canonical IDs)
type LinkData = {
  id: string;
  fromStepId: string;
  toStepId: string;
  createdAt: number;
};

import { GridStep, HOVER_INTENT_MS } from "./GridStep";
import { Minimap } from "./Minimap";
import { matchSteps } from "./stepSearch";
import { useStepMapKeyboardNav } from "./useStepMapKeyboardNav";

export const gridPadding = DENSITY_SPECS.normal.padding;
export const gridStepHeight = DENSITY_SPECS.normal.cardHeight;

type StepMapLayout = Map<string, { row: number; col: number }>;

const Root = styled("div")`
  position: relative;
  height: 100%;
`;

const Scroll = styled("div")`
  height: 100%;
  display: flex;
  overflow: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  cursor: grab;
  outline: none;
  mask-image:
    linear-gradient(
      to bottom,
      transparent,
      black var(--fade-top, 0px),
      black calc(100% - var(--fade-bottom, 0px)),
      transparent
    ),
    linear-gradient(
      to right,
      transparent,
      black var(--fade-left, 0px),
      black calc(100% - var(--fade-right, 0px)),
      transparent
    );
  mask-composite: intersect;
  &.panning {
    cursor: grabbing;
    user-select: none;
  }
`;

const Canvas = styled("div")`
  position: relative;
  flex: none;
  margin: auto auto auto 0;

  /* A lone step has no flow to anchor left, so centre it instead of stranding
     it against the left edge. */
  &[data-center="true"] {
    margin: auto;
  }

  & > a {
    transition:
      opacity 200ms ease,
      filter 200ms ease;
  }

  /* Geometry morphs only once the branch data has settled, so a streaming
     load snaps into place instead of animating every chunk. */
  &[data-settled="true"] {
    transition:
      width 200ms ease,
      height 200ms ease;
  }
  &[data-settled="true"] > a {
    transition:
      left 200ms ease,
      top 200ms ease,
      width 200ms ease,
      height 200ms ease,
      padding 200ms ease,
      border-radius 200ms ease,
      opacity 200ms ease,
      filter 200ms ease;
  }
  &[data-settled="true"] > div {
    transition:
      top 200ms ease,
      left 200ms ease;
  }
  &[data-settled="true"] svg path {
    transition: d 200ms ease;
  }
`;

// Toast-like entrance, mirrored to rise from below the pane's bottom edge.
const controlsSlideUp = keyframes`
  0% {
    transform: translateY(calc(100% + 12px));
    opacity: 0;
  }
  60% {
    transform: translateY(-2px);
    opacity: 1;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

// Mirror of controlsSlideUp for top-anchored chrome: descends from above.
const controlsSlideDown = keyframes`
  0% {
    transform: translateY(calc(-100% - 12px));
    opacity: 0;
  }
  60% {
    transform: translateY(2px);
    opacity: 1;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const MapControls = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  width: 144px;
  padding: 0 4px;
  border-radius: 9999px;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  box-sizing: border-box;
  opacity: 0;
  transform: translateY(calc(100% + 12px));
  pointer-events: none;
  transition:
    opacity 160ms ease,
    transform 160ms ease,
    width 220ms cubic-bezier(0.22, 0.68, 0.35, 1.05);
  &[data-search-open="true"] {
    width: 260px;
  }
  &[data-visible="true"] {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
    animation: ${controlsSlideUp} 300ms cubic-bezier(0.22, 0.68, 0.35, 1.05);
  }
  &:focus-within {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
`;

const SearchRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  padding-left: 4px;
`;

const MatchCount = styled("div")`
  flex: none;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: var(--text-primary);
  opacity: 0.4;
  white-space: nowrap;
`;

// Top-left "back" pill: a lone button that collapses the expanded map, mirroring
// the controls pill's treatment but descending from the top edge.
const BackControl = styled("div")`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 3;
  display: flex;
  align-items: center;
  height: 32px;
  padding: 0 4px;
  border-radius: 9999px;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  box-sizing: border-box;
  opacity: 0;
  transform: translateY(calc(-100% - 12px));
  pointer-events: none;
  transition:
    opacity 160ms ease,
    transform 160ms ease;
  &[data-visible="true"] {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
    animation: ${controlsSlideDown} 300ms cubic-bezier(0.22, 0.68, 0.35, 1.05);
  }
  &:focus-within {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
`;

// Bottom-right chrome row: the controls pill, with the "return to selected step"
// locator as a separate pill to its left (mirrors /chat's RightControls).
const ChromeRow = styled("div")`
  position: absolute;
  bottom: 12px;
  right: 12px;
  z-index: 3;
  display: flex;
  align-items: center;
`;

// The "return to the selected step" affordance: its own pill pinned to the
// controls' left (absolutely, so it never reserves flow space and the controls
// always hug the corner). Shown once the selected step is panned off-screen, its
// arrow pointing the way back. It slides in and out from below exactly like the
// controls (same transition/keyframe), just delayed so it lands after them.
// Visibility and the arrow's rotation are driven imperatively on scroll (see the
// effect) so panning a large map never re-renders every card.
const LocatorSlot = styled("div")`
  position: absolute;
  top: 0;
  right: calc(100% + 6px);
  display: flex;
  align-items: center;
  height: 32px;
  padding: 0 4px;
  border-radius: 9999px;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  box-sizing: border-box;
  opacity: 0;
  transform: translateY(calc(100% + 12px));
  pointer-events: none;
  transition:
    opacity 160ms ease,
    transform 160ms ease;
  &[data-visible="true"] {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
    animation: ${controlsSlideUp} 300ms cubic-bezier(0.22, 0.68, 0.35, 1.05)
      90ms backwards;
  }
`;

// A 24px button inside the pill, matching PanelToggleLink (it's a press action,
// not a navigation link, so it can't reuse that styled Link directly).
const LocatorButton = styled(PressButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  color: var(--text-primary);
  &:hover {
    background: var(--alpha4);
  }
  &:active {
    background: var(--alpha8);
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
`;

const BandLabel = styled("div")`
  position: absolute;
  pointer-events: none;
`;

const PanelToggleLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  color: var(--text-primary);
  &:hover {
    background: var(--alpha4);
  }
  &:active {
    background: var(--alpha8);
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
  & svg {
    opacity: 0.3;
  }
  & .inner {
    transition:
      height 180ms cubic-bezier(0.4, 0, 0.2, 1),
      y 180ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  &[data-map-state="expanded"] .inner {
    height: 8px;
    y: 4px;
  }
  &[data-map-state="open"][data-preview="true"] .inner {
    height: 8px;
    y: 4px;
  }
  &[data-map-state="expanded"][data-preview="true"] .inner {
    height: 4px;
    y: 8px;
  }
  & .bar-top,
  & .bar-bottom {
    transition: y 180ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  &[data-spacing-state="cosy"] .bar-top {
    y: 3.5px;
  }
  &[data-spacing-state="cosy"] .bar-bottom {
    y: 11px;
  }
  &[data-spacing-state="spacious"] .bar-top {
    y: 2px;
  }
  &[data-spacing-state="spacious"] .bar-bottom {
    y: 12.5px;
  }
  /* Hover previews the next level in the cycle (compact → cosy → spacious). */
  &[data-spacing-state="compact"][data-preview="true"] .bar-top {
    y: 3.5px;
  }
  &[data-spacing-state="compact"][data-preview="true"] .bar-bottom {
    y: 11px;
  }
  &[data-spacing-state="cosy"][data-preview="true"] .bar-top {
    y: 2px;
  }
  &[data-spacing-state="cosy"][data-preview="true"] .bar-bottom {
    y: 12.5px;
  }
  &[data-spacing-state="spacious"][data-preview="true"] .bar-top {
    y: 5px;
  }
  &[data-spacing-state="spacious"][data-preview="true"] .bar-bottom {
    y: 9.5px;
  }
  /* One 3-segment path: a right-angle step (tidy) whose corners relax into a
     flowing curve (loose). Same command structure, so the d tweens. */
  & .wire {
    d: path("M 2 4 C 4 4, 6 4, 8 4 C 8 7, 8 9, 8 12 C 10 12, 12 12, 14 12");
    transition: d 180ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  &[data-connector-state="loose"] .wire {
    d: path("M 2 4 C 4 4, 5 5, 6 6 C 7 7, 9 9, 10 10 C 11 11, 13 12, 14 12");
  }
  &[data-connector-state="tidy"][data-preview="true"] .wire {
    d: path("M 2 4 C 4 4, 5 5, 6 6 C 7 7, 9 9, 10 10 C 11 11, 13 12, 14 12");
  }
  &[data-connector-state="loose"][data-preview="true"] .wire {
    d: path("M 2 4 C 4 4, 6 4, 8 4 C 8 7, 8 9, 8 12 C 10 12, 12 12, 14 12");
  }
`;

const dashFlow = keyframes`
  to {
    stroke-dashoffset: -3;
  }
`;

const Lines = styled("svg")`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: visible;

  .active {
    stroke-dasharray: 1 2;
    animation: ${dashFlow} 700ms linear infinite;
  }

  .dimmed {
    opacity: 0.6;
    filter: blur(3px);
    transition:
      opacity 200ms ease,
      filter 200ms ease;
  }
`;

type ContextMenuState = {
  step: StepData;
  x: number;
  y: number;
} | null;

type Props = {
  layout: StepMapLayout;
  steps: StepData[];
  links: LinkData[];
  workflow: WorkflowData;
  spaceSlug: string;
  selectedStep: string | null;
  branchId: string;
  commitId: string;
  isOnMainBranch: boolean;
  isExpanded: boolean;
  densityOverride: StepMapDensity | null;
  spacingLevel: SpacingLevel;
  curvedConnectors: boolean;
  dataSettled: boolean;
  isReadOnly?: boolean;
  canWrite: boolean;
  aiPresentOnStepName?: string;
  presentByStep: Map<string, Set<string>>;
  enableSounds: boolean;
  changedCanonicalIds: Set<string>;
  highlightedCanonicalIds?: Set<string> | null;
  hoveredCanonicalId?: string | null;
  onStepHover?: (canonicalId: string | null) => void;
};

const emptySet = new Set<string>();
const emptyStringArray: string[] = [];

// The size button cycles auto → small → medium → large ("" clears the param
// back to auto); "large" is the user-facing word for the normal density.
const DENSITY_PARAM_CYCLE = ["", "small", "medium", "large"] as const;
const DENSITY_LABELS: Record<string, string> = {
  "": "Auto",
  small: "Small",
  medium: "Medium",
  large: "Large",
};

// The spacing button cycles through three progressively roomier gap levels.
const SPACING_CYCLE = ["compact", "cosy", "spacious"] as const;
const SPACING_LABELS: Record<SpacingLevel, string> = {
  compact: "Compact",
  cosy: "Cosy",
  spacious: "Spacious",
};

export const StepMap = memo(function StepMap({
  layout,
  steps,
  links,
  workflow,
  spaceSlug,
  selectedStep,
  branchId,
  commitId,
  isOnMainBranch,
  isExpanded,
  densityOverride,
  spacingLevel,
  curvedConnectors,
  dataSettled,
  isReadOnly = false,
  canWrite,
  aiPresentOnStepName,
  presentByStep,
  enableSounds,
  changedCanonicalIds,
  highlightedCanonicalIds,
  hoveredCanonicalId,
  onStepHover,
}: Props) {
  const customDomain = useTenantCustomDomain();
  const externalPublicUrl = publicUrl();
  const domain = publicDomain();
  // Resolve stable identifier to physical ID for layout/ref operations
  const selectedStepId =
    steps.find((s) => s.canonicalId === selectedStep)?.id ?? null;
  const hoveredStepId =
    steps.find((s) => s.canonicalId === hoveredCanonicalId)?.id ?? null;
  const [hoverDirection, setHoverDirection] = useState<"up" | "down" | "both">(
    "both"
  );
  const [hoveredLinkId, setHoveredLinkId] = useState<string | null>(null);
  const linkHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (linkHoverTimerRef.current) clearTimeout(linkHoverTimerRef.current);
    },
    []
  );

  // The focused step and its up/downstream flow. Focus is the hovered
  // connector's two endpoints, else the hovered step (whose edge picks the
  // direction — left edge = upstream, right edge = downstream, middle = both),
  // else the selected step (always both). An edge or connector hover dims
  // everything unrelated; a middle hover or bare selection just lights the
  // flow. Physical step ids equal canonical ids here (both are the step
  // directory).
  const focusGraph = useMemo(() => {
    if (hoveredLinkId) {
      const link = links.find((l) => l.id === hoveredLinkId);
      if (!link) return null;
      const canonicalById = new Map(steps.map((s) => [s.id, s.canonicalId]));
      const relatedStepCids = new Set(
        [link.fromStepId, link.toStepId].flatMap((id) => {
          const c = canonicalById.get(id);
          return c ? [c] : [];
        })
      );
      return {
        relatedLinkIds: new Set([hoveredLinkId]),
        relatedStepCids,
        dimUnrelated: true,
      };
    }
    const focusStepId = hoveredStepId ?? selectedStepId;
    if (!focusStepId) return null;
    const direction = hoveredStepId ? hoverDirection : "both";

    const relatedStepIds = new Set<string>([focusStepId]);
    const relatedLinkIds = new Set<string>();
    const walk = (dir: "up" | "down") => {
      const visited = new Set<string>();
      const queue = [focusStepId];
      while (queue.length > 0) {
        const stepId = queue.pop()!;
        if (visited.has(stepId)) continue;
        visited.add(stepId);
        relatedStepIds.add(stepId);
        for (const l of links) {
          if (dir === "up" && l.toStepId === stepId) {
            relatedLinkIds.add(l.id);
            queue.push(l.fromStepId);
          } else if (dir === "down" && l.fromStepId === stepId) {
            relatedLinkIds.add(l.id);
            queue.push(l.toStepId);
          }
        }
      }
    };
    if (direction !== "down") walk("up");
    if (direction !== "up") walk("down");

    const canonicalById = new Map(steps.map((s) => [s.id, s.canonicalId]));
    const relatedStepCids = new Set(
      [...relatedStepIds].flatMap((id) => {
        const c = canonicalById.get(id);
        return c ? [c] : [];
      })
    );
    return {
      relatedLinkIds,
      relatedStepCids,
      dimUnrelated: direction !== "both",
    };
  }, [
    hoveredLinkId,
    hoveredStepId,
    hoverDirection,
    selectedStepId,
    links,
    steps,
  ]);

  const [contextMenu, setContextMenu] = useState<ContextMenuState>(null);
  const [gridRef, setGridRef] = useState<HTMLDivElement | null>(null);
  const scrollElRef = useRef<HTMLDivElement | null>(null);
  const assignScrollRef = useCallback((el: HTMLDivElement | null) => {
    scrollElRef.current = el;
    setGridRef(el);
  }, []);
  const canvasRef = useRef<HTMLDivElement>(null);
  const linesRef = useRef<SVGSVGElement>(null);
  // Persistent path elements keyed by link id, so the .active animation
  // keeps running across geometry changes instead of restarting each time.
  const pathRefsRef = useRef(
    new Map<
      string,
      {
        bg: SVGPathElement;
        hit: SVGPathElement;
        fg: { casing: SVGPathElement; dash: SVGPathElement } | null;
      }
    >()
  );

  // Track new cards for entrance animation — use canonicalId (stable across edits)
  const seenCanonicalIdsRef = useRef<Set<string>>(new Set());
  const [enteringIds, setEnteringIds] = useState<Set<string>>(new Set());
  const hasRevealedRef = useRef(false);

  useScrollFade({ scrollRef: scrollElRef });

  // Geometry morphs arm one frame after the steps settle: blobs hydrate in
  // chunks even after the branch query reports complete, and each chunk's
  // relayout must snap rather than animate the map into place.
  const [morphReady, setMorphReady] = useState(false);
  useEffect(() => {
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    setMorphReady(false);
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setMorphReady(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [steps, links]);

  // The toggle icon previews its result state on hover, like the sidebar's;
  // suppressed after a click so it doesn't immediately preview the reverse.
  const [toggleHovered, setToggleHovered] = useState(false);
  const [togglePreviewSuppressed, setTogglePreviewSuppressed] = useState(false);
  const togglePreview = toggleHovered && !togglePreviewSuppressed;
  const [spacingHovered, setSpacingHovered] = useState(false);
  const [spacingPreviewSuppressed, setSpacingPreviewSuppressed] =
    useState(false);
  const spacingPreview = spacingHovered && !spacingPreviewSuppressed;
  const [connectorHovered, setConnectorHovered] = useState(false);
  const [connectorPreviewSuppressed, setConnectorPreviewSuppressed] =
    useState(false);
  const connectorPreview = connectorHovered && !connectorPreviewSuppressed;

  // Collapsed view: the controls slide in only while the pointer is over the
  // map. Expanded view: they persist. An open search pins them too.
  const [mapHovered, setMapHovered] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [previousSearchQuery, setPreviousSearchQuery] = useState(searchQuery);
  if (previousSearchQuery !== searchQuery) {
    setPreviousSearchQuery(searchQuery);
    setCurrentMatchIndex(0);
  }
  const searchInputRef = useRef<HTMLInputElement>(null);
  const showControls = isExpanded || mapHovered || searchOpen;

  const densityParam =
    densityOverride === null
      ? ""
      : densityOverride === "normal"
        ? "large"
        : densityOverride;
  const nextDensityParam =
    DENSITY_PARAM_CYCLE[
      (DENSITY_PARAM_CYCLE.indexOf(
        densityParam as (typeof DENSITY_PARAM_CYCLE)[number]
      ) +
        1) %
        DENSITY_PARAM_CYCLE.length
    ] ?? "";
  const densityLabel = DENSITY_LABELS[densityParam] ?? "Auto";
  const nextSpacing =
    SPACING_CYCLE[
      (SPACING_CYCLE.indexOf(spacingLevel) + 1) % SPACING_CYCLE.length
    ] ?? "compact";

  // The pane's inner height drives density and its width feeds the capped
  // card stretch; floored so subpixel resize jitter can't flap a threshold.
  const [paneSize, setPaneSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  useLayoutEffect(() => {
    if (!gridRef) return;
    const measure = () =>
      setPaneSize((prev) => {
        const width = Math.floor(gridRef.clientWidth);
        const height = Math.floor(gridRef.clientHeight);
        return prev && prev.width === width && prev.height === height
          ? prev
          : { width, height };
      });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(gridRef);
    return () => observer.disconnect();
  }, [gridRef]);

  const { density, spec, geometry } = useMemo(() => {
    if (isExpanded) {
      const density = densityOverride ?? "normal";
      const spec = specForSpacing(density, spacingLevel);
      return {
        density,
        spec,
        geometry: computeStepMapGeometry(
          layout,
          links,
          spec,
          paneSize?.width,
          paneSize?.height
        ),
      };
    }
    if (densityOverride) {
      const spec = specForSpacing(densityOverride, spacingLevel);
      return {
        density: densityOverride,
        spec,
        geometry: computeStepMapGeometry(
          layout,
          links,
          spec,
          paneSize?.width,
          paneSize?.height
        ),
      };
    }
    return fitStepMap(
      layout,
      links,
      paneSize ?? { width: Infinity, height: Infinity },
      spacingLevel
    );
  }, [isExpanded, densityOverride, spacingLevel, layout, links, paneSize]);

  // Point the off-screen locator at the selected step, imperatively: toggle its
  // visibility and rotate its arrow on every scroll/resize frame without a React
  // render, so panning a large map stays cheap.
  const locatorSlotRef = useRef<HTMLDivElement>(null);
  const locatorArrowRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const slot = locatorSlotRef.current;
    const arrow = locatorArrowRef.current;
    if (!gridRef || !slot || !arrow || !selectedStepId) return;
    const rect = geometry.rects.get(selectedStepId);
    // Only surface the locator alongside the controls (same hover/expanded
    // gate) so it sits to their left rather than floating in an empty corner.
    if (!rect || !showControls) {
      slot.removeAttribute("data-visible");
      return;
    }
    let raf = 0;
    const apply = () => {
      raf = 0;
      const vl = gridRef.scrollLeft;
      const vt = gridRef.scrollTop;
      const vw = gridRef.clientWidth;
      const vh = gridRef.clientHeight;
      const onScreen =
        rect.right > vl &&
        rect.left < vl + vw &&
        rect.bottom > vt &&
        rect.top < vt + vh;
      if (onScreen) {
        slot.removeAttribute("data-visible");
        return;
      }
      const dx = (rect.left + rect.right) / 2 - (vl + vw / 2);
      const dy = rect.centerY - (vt + vh / 2);
      arrow.style.transform = `rotate(${Math.atan2(dy, dx) * (180 / Math.PI)}deg)`;
      slot.setAttribute("data-visible", "true");
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(apply);
    };
    // Defer the first read a frame so the sibling auto-centre effect's
    // scrollIntoView settles first — otherwise the arrow flashes on selection.
    schedule();
    gridRef.addEventListener("scroll", schedule, { passive: true });
    const observer = new ResizeObserver(schedule);
    observer.observe(gridRef);
    return () => {
      gridRef.removeEventListener("scroll", schedule);
      observer.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [gridRef, selectedStepId, geometry, showControls]);

  const scrollToSelectedStep = useCallback(() => {
    const el = scrollElRef.current;
    if (!el || !selectedStepId) return;
    const rect = geometry.rects.get(selectedStepId);
    if (!rect) return;
    el.scrollTo({
      left: (rect.left + rect.right) / 2 - el.clientWidth / 2,
      top: rect.centerY - el.clientHeight / 2,
      behavior: "smooth",
    });
  }, [selectedStepId, geometry]);

  const searchActive = searchOpen && searchQuery.trim().length > 0;
  const searchMatches = useMemo(
    () => (searchActive ? matchSteps(steps, searchQuery, geometry.rects) : []),
    [searchActive, searchQuery, steps, geometry]
  );
  const matchCount = searchMatches.length;
  const currentMatch =
    matchCount > 0 ? searchMatches[currentMatchIndex % matchCount] : undefined;
  const currentMatchCanonicalId = currentMatch?.canonicalId ?? null;

  // Search reuses the run-highlight dim path: matches stay lit, everything else
  // dims. An active search overrides the caller's highlight; zero matches (an
  // empty set) dims every card, which is the "no results" signal.
  const searchMatchCids = useMemo(
    () =>
      searchActive ? new Set(searchMatches.map((s) => s.canonicalId)) : null,
    [searchActive, searchMatches]
  );
  const effectiveHighlightedCanonicalIds = useMemo(
    () => searchMatchCids ?? highlightedCanonicalIds ?? null,
    [searchMatchCids, highlightedCanonicalIds]
  );

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery("");
    if (isExpanded) scrollElRef.current?.focus({ preventScroll: true });
  }, [isExpanded, setSearchOpen, setSearchQuery]);

  const stepMatch = useCallback(
    (delta: number) => {
      setCurrentMatchIndex(
        matchCount === 0
          ? 0
          : (currentMatchIndex + delta + matchCount) % matchCount
      );
    },
    [currentMatchIndex, matchCount]
  );

  useEffect(() => {
    if (!searchOpen) return;
    const id = requestAnimationFrame(() =>
      searchInputRef.current?.focus({ preventScroll: true })
    );
    return () => cancelAnimationFrame(id);
  }, [searchOpen]);

  // Center the active match whenever it changes — narrowing the query or
  // stepping with Enter. Reads geometry from a ref and keys only on the match
  // id, so a pane resize or Zero-driven relayout (both of which change geometry
  // identity) can't yank a panned-away viewport back onto the match.
  const currentMatchId = currentMatch?.id;
  const geometryRef = useRef(geometry);
  useEffect(() => {
    geometryRef.current = geometry;
  }, [geometry]);
  useEffect(() => {
    const el = scrollElRef.current;
    if (!currentMatchId || !el) return;
    const rect = geometryRef.current.rects.get(currentMatchId);
    if (!rect) return;
    el.scrollTo({
      left: (rect.left + rect.right) / 2 - el.clientWidth / 2,
      top: rect.centerY - el.clientHeight / 2,
      behavior: "smooth",
    });
  }, [currentMatchId]);

  // Canvas-style drag-to-pan
  const panRef = useRef<{
    x: number;
    y: number;
    scrollLeft: number;
    scrollTop: number;
  } | null>(null);

  useEffect(() => {
    if (!gridRef) return;

    const onMouseDown = (e: MouseEvent) => {
      // While expanded, clicking the map hands it keyboard focus (the chat
      // composer autofocuses otherwise) so Esc/Enter/arrows drive the map. In
      // the collapsed split view we leave focus where it is, so a click doesn't
      // blur what the user is typing — arrow-key nav uses a window listener.
      if (isExpanded) gridRef.focus({ preventScroll: true });
      // Middle-click anywhere, or left-click on canvas/SVG background
      const isBackground =
        e.target === gridRef ||
        e.target === canvasRef.current ||
        !!linesRef.current?.contains(e.target as Node);
      if (e.button === 1 || (e.button === 0 && isBackground)) {
        e.preventDefault();
        panRef.current = {
          x: e.clientX,
          y: e.clientY,
          scrollLeft: gridRef.scrollLeft,
          scrollTop: gridRef.scrollTop,
        };
        gridRef.classList.add("panning");
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!panRef.current) return;
      gridRef.scrollLeft =
        panRef.current.scrollLeft - (e.clientX - panRef.current.x);
      gridRef.scrollTop =
        panRef.current.scrollTop - (e.clientY - panRef.current.y);
    };

    const onMouseUp = (e: MouseEvent) => {
      const pan = panRef.current;
      if (pan) {
        panRef.current = null;
        gridRef.classList.remove("panning");
        const moved = Math.hypot(e.clientX - pan.x, e.clientY - pan.y);
        if (selectedStep !== null && e.button === 0 && moved < 4) {
          navigate(routes.space.workflow, {
            spaceId: workflow.spaceId,
            workflowId: workflow.id,
            step: "",
            file: "",
          });
        }
      }
    };

    gridRef.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      gridRef.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [gridRef, selectedStep, workflow.id, workflow.spaceId, isExpanded]);

  // Arrow keys follow what's on screen: the standalone band renders below the
  // flow, so its layout rows are remapped to the end of the visual order.
  const navLayout = useMemo(() => {
    const band = geometry.band;
    if (!band) return layout;
    const rowCount =
      Math.max(...Array.from(layout.values()).map((p) => p.row), -1) + 1;
    const remapped = new Map<string, { row: number; col: number }>();
    for (const [id, pos] of layout) {
      remapped.set(id, {
        row:
          pos.row < band.rowCount
            ? pos.row + rowCount - band.rowCount
            : pos.row - band.rowCount,
        col: pos.col,
      });
    }
    return remapped;
  }, [layout, geometry.band]);

  useStepMapKeyboardNav({
    layout: navLayout,
    steps,
    selectedStep,
    spaceId: workflow.spaceId,
    workflowId: workflow.id,
    isExpanded,
  });

  useEffect(() => {
    if (!selectedStepId || !gridRef) return;
    const el = gridRef.querySelector(
      `[data-step-id="${CSS.escape(selectedStepId)}"]`
    );
    el?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [selectedStepId, gridRef, geometry]);

  // While expanded the map holds DOM focus (re-asserted after selection
  // changes, which hand focus to the chat composer) so Esc, Enter, and the
  // arrow keys drive the map instead of typing into the chat.
  useEffect(() => {
    if (!isExpanded || !gridRef) return;
    gridRef.focus({ preventScroll: true });
  }, [isExpanded, gridRef, selectedStepId]);

  // Detect genuinely new steps (first creation only, not edits/executions).
  // Uses canonicalId (stable across versions) and a persistent "seen" set
  // so each step only triggers the pop once, ever.
  useEffect(() => {
    if (!hasRevealedRef.current) {
      // Still loading — seed the seen set, don't trigger anything
      for (const s of steps) seenCanonicalIdsRef.current.add(s.canonicalId);
      return;
    }

    const newStepIds: string[] = [];
    for (const s of steps) {
      if (!seenCanonicalIdsRef.current.has(s.canonicalId)) {
        seenCanonicalIdsRef.current.add(s.canonicalId);
        newStepIds.push(s.id);
      }
    }

    if (newStepIds.length > 0) {
      const ids = new Set(newStepIds);
      // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
      setEnteringIds(ids);
      playPop();
      const timer = setTimeout(() => setEnteringIds(new Set()), 300);
      return () => clearTimeout(timer);
    }
  }, [steps]);

  // Write connector paths directly to the SVG DOM: reusing path elements per
  // link id keeps the dashFlow animation from restarting when geometry moves.
  useEffect(() => {
    const svg = linesRef.current;
    if (!svg) return;

    // Standalone-band steps carry no links, so routing from the flow's rects
    // alone keeps loopback channels hugging the flow instead of arcing around
    // the band.
    let routingRects = geometry.rects;
    if (geometry.band) {
      const bandRowCount = geometry.band.rowCount;
      routingRects = new Map(
        [...geometry.rects].filter(([, r]) => r.row >= bandRowCount)
      );
    }
    const paths = routeConnectors(routingRects, links, curvedConnectors);

    // Links on the focused step's flow animate; when the focus comes from a
    // hover, everything unrelated dims.
    const relatedLinkIds = focusGraph?.relatedLinkIds ?? emptySet;
    const dimUnrelated = focusGraph?.dimUnrelated ?? false;

    const ns = "http://www.w3.org/2000/svg";
    const pathRefs = pathRefsRef.current;

    // The CSS d property (where supported) lets path geometry transition in
    // step with the cards; the attribute stays as the non-animating fallback.
    const setPath = (el: SVGPathElement, d: string) => {
      el.setAttribute("d", d);
      el.style.setProperty("d", `path("${d}")`);
    };

    const createPath = (stroke: string, cls?: string, width = 1) => {
      const el = document.createElementNS(ns, "path");
      el.setAttribute("stroke", stroke);
      el.setAttribute("stroke-width", String(width));
      el.setAttribute("fill", "none");
      el.setAttribute("stroke-linejoin", "round");
      el.setAttribute("stroke-linecap", "round");
      if (cls) el.classList.add(cls);
      return el;
    };

    // Build a physical-ID → canonical-ID map so we can check which links
    // connect steps that are part of the highlighted run.
    const idToCanonical = new Map<string, string>();
    if (effectiveHighlightedCanonicalIds) {
      for (const s of steps) idToCanonical.set(s.id, s.canonicalId);
    }
    const isLinkDimmed = (p: {
      id: string;
      fromStepId: string;
      toStepId: string;
    }) => {
      if (dimUnrelated) return !relatedLinkIds.has(p.id);
      if (!effectiveHighlightedCanonicalIds) return false;
      const fromCid = idToCanonical.get(p.fromStepId);
      const toCid = idToCanonical.get(p.toStepId);
      return (
        !fromCid ||
        !toCid ||
        !effectiveHighlightedCanonicalIds.has(fromCid) ||
        !effectiveHighlightedCanonicalIds.has(toCid)
      );
    };

    // Drop any link elements that no longer exist.
    const liveIds = new Set(paths.map((p) => p.id));
    for (const [id, refs] of pathRefs) {
      if (!liveIds.has(id)) {
        refs.bg.remove();
        refs.hit.remove();
        refs.fg?.casing.remove();
        refs.fg?.dash.remove();
        pathRefs.delete(id);
      }
    }

    // Reconcile bgs first. New bgs are inserted at the front so they sit
    // beneath any existing fgs (later siblings paint on top in SVG).
    for (const p of paths) {
      let refs = pathRefs.get(p.id);
      if (!refs) {
        const bg = createPath("var(--gray-400)");
        svg.insertBefore(bg, svg.firstChild);
        const hit = createPath("transparent", undefined, 14);
        hit.style.pointerEvents = "stroke";
        const linkId = p.id;
        hit.addEventListener("mouseenter", () => {
          if (linkHoverTimerRef.current) {
            clearTimeout(linkHoverTimerRef.current);
          }
          linkHoverTimerRef.current = setTimeout(() => {
            linkHoverTimerRef.current = null;
            setHoveredLinkId(linkId);
          }, HOVER_INTENT_MS);
        });
        hit.addEventListener("mouseleave", () => {
          if (linkHoverTimerRef.current) {
            clearTimeout(linkHoverTimerRef.current);
            linkHoverTimerRef.current = null;
          }
          setHoveredLinkId((cur) => (cur === linkId ? null : cur));
        });
        svg.appendChild(hit);
        refs = { bg, hit, fg: null };
        pathRefs.set(p.id, refs);
      }
      setPath(refs.bg, p.d);
      refs.bg.classList.toggle("dimmed", isLinkDimmed(p));
      setPath(refs.hit, p.d);
    }

    // Reconcile fgs (active animated dashes) on top. Each dash gets a
    // surface-colored casing appended directly beneath it — not all casings
    // then all dashes — so a dash's gaps reveal its own casing (canvas
    // color) rather than a crossing line, keeping overlapping flow lines
    // legible. Reusing existing elements keeps the dashFlow animation from
    // restarting.
    for (const p of paths) {
      const refs = pathRefs.get(p.id)!;
      if (relatedLinkIds.has(p.id)) {
        if (!refs.fg) {
          const casing = createPath("var(--surface-primary)", undefined, 3);
          const dash = createPath("var(--gray-1000)", "active");
          svg.appendChild(casing);
          svg.appendChild(dash);
          refs.fg = { casing, dash };
        }
        setPath(refs.fg.casing, p.d);
        setPath(refs.fg.dash, p.d);
      } else if (refs.fg) {
        refs.fg.casing.remove();
        refs.fg.dash.remove();
        refs.fg = null;
      }
    }
  }, [
    geometry,
    links,
    focusGraph,
    effectiveHighlightedCanonicalIds,
    steps,
    curvedConnectors,
  ]);

  // Mark as revealed on first mount so new-step detection starts working
  useEffect(() => {
    hasRevealedRef.current = true;
  }, []);

  const incomingLinksMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const link of links) {
      const existing = map.get(link.toStepId);
      if (existing) {
        existing.push(link.fromStepId);
      } else {
        map.set(link.toStepId, [link.fromStepId]);
      }
    }
    return map;
  }, [links]);

  // Transitive reach in each direction, so an edge tooltip's count matches the
  // whole flow its hover lights up (see focusGraph's walk), not just the
  // immediate neighbours.
  const { upstreamCounts, downstreamCounts } = useMemo(() => {
    const forward = new Map<string, string[]>();
    const backward = new Map<string, string[]>();
    const nodeIds = new Set<string>();
    const push = (map: Map<string, string[]>, key: string, value: string) => {
      const existing = map.get(key);
      if (existing) existing.push(value);
      else map.set(key, [value]);
    };
    for (const link of links) {
      push(forward, link.fromStepId, link.toStepId);
      push(backward, link.toStepId, link.fromStepId);
      nodeIds.add(link.fromStepId);
      nodeIds.add(link.toStepId);
    }
    const reachCount = (start: string, adj: Map<string, string[]>) => {
      const visited = new Set<string>();
      const queue = [start];
      while (queue.length > 0) {
        const id = queue.pop()!;
        for (const next of adj.get(id) ?? emptyStringArray) {
          if (!visited.has(next)) {
            visited.add(next);
            queue.push(next);
          }
        }
      }
      visited.delete(start);
      return visited.size;
    };
    const upstreamCounts = new Map<string, number>();
    const downstreamCounts = new Map<string, number>();
    for (const id of nodeIds) {
      upstreamCounts.set(id, reachCount(id, backward));
      downstreamCounts.set(id, reachCount(id, forward));
    }
    return { upstreamCounts, downstreamCounts };
  }, [links]);

  const stepColorMap = useMemo(
    () => new Map(steps.map((s) => [s.id, s.color])),
    [steps]
  );

  const stepNameMap = useMemo(
    () => new Map(steps.map((s) => [s.id, s.name])),
    [steps]
  );

  // Stable handlers so memoized GridSteps don't re-render just because the map
  // re-rendered — only cards whose own props change do.
  const handleContextMenu = useCallback(
    (e: ReactMouseEvent, s: StepData) => {
      if (isReadOnly) return;
      setContextMenu({ step: s, x: e.clientX, y: e.clientY });
    },
    [isReadOnly]
  );
  const handleHighlightHover = useCallback(
    (
      canonicalId: string,
      hovered: boolean,
      direction: "up" | "down" | "both"
    ) => {
      onStepHover?.(hovered ? canonicalId : null);
      setHoverDirection(hovered ? direction : "both");
    },
    [onStepHover]
  );

  return (
    <Root
      onPointerEnter={() => setMapHovered(true)}
      onPointerLeave={() => setMapHovered(false)}
    >
      <Scroll ref={assignScrollRef} tabIndex={-1}>
        <Canvas
          ref={canvasRef}
          data-center={steps.length === 1 ? "true" : undefined}
          data-settled={
            dataSettled && morphReady && paneSize !== null ? "true" : undefined
          }
          style={
            paneSize
              ? { width: geometry.width, height: geometry.height }
              : undefined
          }
        >
          {paneSize &&
            steps.map((step) => {
              const rect = geometry.rects.get(step.id);
              if (!rect) return null;

              const isSelected = selectedStep === step.canonicalId;
              // Clicking a card in the expanded overview dives into the step:
              // it selects and collapses back to the split view in one go.
              const to = isExpanded
                ? makeUrl(routes.space.workflow, {
                    spaceId: workflow.spaceId,
                    workflowId: workflow.id,
                    step: step.canonicalId,
                    file: recallStepFile(workflow.id, step.canonicalId) ?? "",
                    map: "",
                  })
                : makeUrl(routes.space.workflow, {
                    spaceId: workflow.spaceId,
                    workflowId: workflow.id,
                    step: isSelected ? "" : step.canonicalId,
                    file: isSelected
                      ? ""
                      : (recallStepFile(workflow.id, step.canonicalId) ?? ""),
                  });

              const isEntering = enteringIds.has(step.id);
              const entranceDelay = isEntering
                ? rect.col * 50 + rect.row * 30
                : undefined;

              return (
                <GridStep
                  rect={rect}
                  density={density}
                  cardRadius={spec.cardRadius}
                  to={to}
                  key={step.canonicalId}
                  step={step}
                  isSelected={isSelected}
                  incomingFromStepIds={
                    incomingLinksMap.get(step.id) ?? emptyStringArray
                  }
                  upstreamCount={upstreamCounts.get(step.id) ?? 0}
                  downstreamCount={downstreamCounts.get(step.id) ?? 0}
                  stepColorMap={stepColorMap}
                  stepNameMap={stepNameMap}
                  onContextMenu={handleContextMenu}
                  isEntering={isEntering}
                  entranceDelay={entranceDelay}
                  isChanged={changedCanonicalIds.has(step.canonicalId)}
                  isDimmed={
                    focusGraph?.dimUnrelated
                      ? !focusGraph.relatedStepCids.has(step.canonicalId)
                      : effectiveHighlightedCanonicalIds != null &&
                        !effectiveHighlightedCanonicalIds.has(step.canonicalId)
                  }
                  isCurrentMatch={
                    currentMatchCanonicalId != null &&
                    step.canonicalId === currentMatchCanonicalId
                  }
                  isAiPresent={step.name === aiPresentOnStepName}
                  onHighlightHover={handleHighlightHover}
                  presentUserIds={presentByStep.get(step.id) ?? emptySet}
                  enableSounds={enableSounds}
                  copyableUrl={getStepCopyableUrl(
                    step,
                    branchId,
                    isOnMainBranch,
                    spaceSlug,
                    externalPublicUrl,
                    domain,
                    customDomain
                  )}
                  copyableEmail={getStepCopyableEmailAddress(
                    step,
                    branchId,
                    isOnMainBranch,
                    spaceSlug,
                    domain
                  )}
                />
              );
            })}

          <Lines ref={linesRef} />

          {geometry.band && (
            <>
              <BandLabel
                style={{
                  left: geometry.band.workflowLabel.left,
                  top: geometry.band.workflowLabel.top,
                }}
              >
                <Text variant="text-smaller-md" color="var(--text-secondary)">
                  Workflow
                </Text>
              </BandLabel>
              <BandLabel
                style={{
                  left: geometry.band.standaloneLabel.left,
                  top: geometry.band.standaloneLabel.top,
                }}
              >
                <Text variant="text-smaller-md" color="var(--text-secondary)">
                  Standalone steps
                </Text>
              </BandLabel>
            </>
          )}

          {contextMenu && (
            <StepContextMenu
              step={contextMenu.step}
              x={contextMenu.x}
              y={contextMenu.y}
              workflowId={workflow.id}
              branchId={branchId}
              commitId={commitId}
              canWrite={canWrite}
              copyableUrl={getStepCopyableUrl(
                contextMenu.step,
                branchId,
                isOnMainBranch,
                spaceSlug,
                externalPublicUrl,
                domain,
                customDomain
              )}
              copyableEmail={getStepCopyableEmailAddress(
                contextMenu.step,
                branchId,
                isOnMainBranch,
                spaceSlug,
                domain
              )}
              onClose={() => setContextMenu(null)}
            />
          )}
        </Canvas>
      </Scroll>
      {isExpanded &&
        paneSize &&
        (geometry.width > paneSize.width ||
          geometry.height > paneSize.height) && (
          <Minimap
            geometry={geometry}
            stepColorMap={stepColorMap}
            scrollEl={gridRef}
          />
        )}
      {isExpanded && (
        <BackControl data-visible={showControls ? "true" : undefined}>
          <Tooltip content="Collapse map">
            <PanelToggleLink
              to={makeUrl(null, { map: "" })}
              aria-label="Collapse map"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                style={{ opacity: 1 }}
              >
                <path
                  d="M12.5 8H4M4 8L8 4M4 8L8 12"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </PanelToggleLink>
          </Tooltip>
        </BackControl>
      )}
      <ChromeRow>
        {selectedStepId && (
          <LocatorSlot ref={locatorSlotRef}>
            <Tooltip content="Scroll to selected step">
              <LocatorButton
                onPress={scrollToSelectedStep}
                aria-label="Scroll to selected step"
              >
                <span
                  ref={locatorArrowRef}
                  style={{
                    display: "flex",
                    opacity: 0.3,
                    transition: "transform 160ms ease-out",
                  }}
                >
                  <Icon name="arrow-right" size={12} />
                </span>
              </LocatorButton>
            </Tooltip>
          </LocatorSlot>
        )}
        <MapControls
          data-visible={showControls ? "true" : undefined}
          data-search-open={searchOpen ? "true" : undefined}
        >
          {searchOpen ? (
            <SearchRow>
              <Icon
                name="search"
                size={16}
                style={{ flex: "none", opacity: 0.3 }}
              />
              <SearchInput
                ref={searchInputRef}
                value={searchQuery}
                placeholder="Search steps…"
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.stopPropagation();
                    e.preventDefault();
                    closeSearch();
                  } else if (e.key === "Enter") {
                    e.preventDefault();
                    stepMatch(e.shiftKey ? -1 : 1);
                  }
                }}
              />
              {searchQuery.trim() && (
                <MatchCount>
                  {matchCount ? (currentMatchIndex % matchCount) + 1 : 0}/
                  {matchCount}
                </MatchCount>
              )}
              <Tooltip content="Close search">
                <LocatorButton
                  onPress={closeSearch}
                  onMouseDown={(e) => e.preventDefault()}
                  aria-label="Close search"
                >
                  <Icon name="x" size={12} style={{ opacity: 0.5 }} />
                </LocatorButton>
              </Tooltip>
            </SearchRow>
          ) : (
            <>
              <Tooltip content="Search steps">
                <LocatorButton
                  onPress={() => setSearchOpen(true)}
                  onMouseDown={(e) => e.preventDefault()}
                  aria-label="Search steps"
                >
                  <Icon name="search" size={16} style={{ opacity: 0.3 }} />
                </LocatorButton>
              </Tooltip>
              <Tooltip content={`Card size: ${densityLabel}`}>
                <PanelToggleLink
                  to={makeUrl(null, { density: nextDensityParam })}
                  aria-label={`Card size: ${densityLabel}`}
                >
                  <EffortIcon
                    level={density === "normal" ? "large" : density}
                    levels={DENSITY_PARAM_CYCLE.slice(1)}
                  />
                </PanelToggleLink>
              </Tooltip>
              <Tooltip content={`Spacing: ${SPACING_LABELS[spacingLevel]}`}>
                <PanelToggleLink
                  to={makeUrl(null, { spacing: nextSpacing })}
                  aria-label={`Spacing: ${SPACING_LABELS[spacingLevel]}`}
                  data-spacing-state={spacingLevel}
                  data-preview={spacingPreview}
                  onMouseEnter={() => setSpacingHovered(true)}
                  onMouseLeave={() => {
                    setSpacingHovered(false);
                    setSpacingPreviewSuppressed(false);
                  }}
                  onMouseDown={() => setSpacingPreviewSuppressed(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect
                      className="bar-top"
                      x="3"
                      y="5"
                      width="10"
                      height="1.5"
                      rx="0.75"
                      fill="currentColor"
                    />
                    <rect
                      x="3"
                      y="7.25"
                      width="10"
                      height="1.5"
                      rx="0.75"
                      fill="currentColor"
                    />
                    <rect
                      className="bar-bottom"
                      x="3"
                      y="9.5"
                      width="10"
                      height="1.5"
                      rx="0.75"
                      fill="currentColor"
                    />
                  </svg>
                </PanelToggleLink>
              </Tooltip>
              <Tooltip
                content={`Connectors: ${curvedConnectors ? "Loose" : "Tidy"}`}
              >
                <PanelToggleLink
                  to={makeUrl(null, {
                    connectors: curvedConnectors ? "tidy" : "loose",
                  })}
                  aria-label={`Connectors: ${curvedConnectors ? "Loose" : "Tidy"}`}
                  data-connector-state={curvedConnectors ? "loose" : "tidy"}
                  data-preview={connectorPreview}
                  onMouseEnter={() => setConnectorHovered(true)}
                  onMouseLeave={() => {
                    setConnectorHovered(false);
                    setConnectorPreviewSuppressed(false);
                  }}
                  onMouseDown={() => setConnectorPreviewSuppressed(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      className="wire"
                      d="M 2 4 C 4 4, 6 4, 8 4 C 8 7, 8 9, 8 12 C 10 12, 12 12, 14 12"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </PanelToggleLink>
              </Tooltip>
              <Tooltip content={isExpanded ? "Collapse map" : "Expand map"}>
                <PanelToggleLink
                  to={makeUrl(null, { map: isExpanded ? "" : "expanded" })}
                  aria-label={isExpanded ? "Collapse map" : "Expand map"}
                  data-map-state={isExpanded ? "expanded" : "open"}
                  data-preview={togglePreview}
                  onMouseEnter={() => setToggleHovered(true)}
                  onMouseLeave={() => {
                    setToggleHovered(false);
                    setTogglePreviewSuppressed(false);
                  }}
                  onMouseDown={() => setTogglePreviewSuppressed(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M11 2C12.6569 2 14 3.34315 14 5L14 11C14 12.6569 12.6569 14 11 14L6 14L5 14C3.34315 14 2 12.6569 2 11L2 5C2 3.34315 3.34315 2 5 2L6 2L11 2Z"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <rect
                      className="inner"
                      x="4"
                      y="8"
                      width="8"
                      height="4"
                      rx="1.5"
                      fill="currentColor"
                    />
                  </svg>
                </PanelToggleLink>
              </Tooltip>
            </>
          )}
        </MapControls>
      </ChromeRow>
    </Root>
  );
});
